import React, { useMemo, useState } from 'react';
import { Transaction } from '../types';
import { usePos } from '../context/PosContext';
import { formatDate } from '../services/datetime';
import './Entry.css';

type Mode = 'bill' | 'nobill';

// Matches sale_return_layout.xml.
const SaleReturn: React.FC = () => {
  const { sales, recordSaleReturn, formatMoney, config } = usePos();
  const [mode, setMode] = useState<Mode>('bill');
  const [search, setSearch] = useState('');
  const [bill, setBill] = useState<Transaction | null>(null);
  const [returnQty, setReturnQty] = useState<Record<string, number>>({});

  const matches = useMemo(() => {
    const t = search.trim().toLowerCase();
    return sales
      .filter((s) => s.status === 'completed' && s.total > 0)
      .filter((s) => !t || s.billNo.toLowerCase().includes(t) || s.customerName?.toLowerCase().includes(t))
      .reverse();
  }, [sales, search]);

  const selectBill = (tx: Transaction) => {
    setBill(tx);
    setReturnQty({});
  };

  const setQty = (productId: string, max: number, val: string) => {
    const q = Math.max(0, Math.min(max, parseFloat(val) || 0));
    setReturnQty((prev) => ({ ...prev, [productId]: q }));
  };

  const returnLines = useMemo(() => {
    if (!bill) return [];
    return bill.items
      .filter((i) => (returnQty[i.productId] ?? 0) > 0)
      .map((i) => ({ ...i, quantity: returnQty[i.productId], total: i.unitPrice * returnQty[i.productId] * (1 - (i.discountPercent ?? 0) / 100) }));
  }, [bill, returnQty]);

  const billAmt = useMemo(() => {
    const sub = returnLines.reduce((s, i) => s + i.total, 0);
    const tax = returnLines.reduce((s, i) => s + i.total * (i.taxRatePercent / 100), 0);
    return sub + tax;
  }, [returnLines]);

  const save = () => {
    if (!bill || !returnLines.length) return;
    recordSaleReturn(bill.billNo, returnLines, `Return of ${bill.billNo}`);
    alert(`Credit note created. Refund ${formatMoney(billAmt)}.`);
    setBill(null);
    setReturnQty({});
    setSearch('');
  };

  return (
    <div className="es-page">
      <div className="es-head">
        <span>Sale Return / Credit Note</span>
        <span className="es-sub">{config.storeName}</span>
      </div>

      <div className="es-toolbar">
        <div className="es-seg">
          <button className={mode === 'bill' ? 'on' : ''} onClick={() => setMode('bill')}>
            By Bill No
          </button>
          <button className={mode === 'nobill' ? 'on' : ''} onClick={() => setMode('nobill')}>
            Without Bill No
          </button>
        </div>
        <div className="es-search" style={{ flex: 1 }}>
          <input placeholder="Search Bill No" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="es-search-btn">🔍</div>
        </div>
      </div>

      {mode === 'nobill' ? (
        <div className="es-items">
          <div className="es-empty">Without-bill returns aren't tracked in this build — use “By Bill No”.</div>
        </div>
      ) : !bill ? (
        <div className="es-items">
          {matches.length === 0 ? (
            <div className="es-empty">No bills found.</div>
          ) : (
            matches.map((tx) => (
              <button key={tx.id} className="es-item" style={{ width: '100%', background: '#fff', border: 'none', textAlign: 'left' }} onClick={() => selectBill(tx)}>
                <span className="es-item-name">
                  {tx.billNo} · {tx.customerName ?? 'Walk-in'} · {formatDate(tx.timestamp)}
                </span>
                <span className="es-item-total">{formatMoney(tx.total)}</span>
              </button>
            ))
          )}
        </div>
      ) : (
        <>
          <div className="es-toolbar" style={{ background: '#fff', borderRadius: 10, padding: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <strong>Customer Details:</strong> {bill.customerName ?? 'Walk-in'}
            <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'var(--color-primary-darker)' }}>INVOICE : {bill.billNo}</span>
          </div>
          <div className="es-items">
            {bill.items.map((i) => {
              const q = returnQty[i.productId] ?? 0;
              const refund = i.unitPrice * q * (1 - (i.discountPercent ?? 0) / 100);
              return (
                <div className="es-item" key={i.productId}>
                  <span className="es-item-name">
                    {i.name} <span style={{ color: 'var(--color-grayish)' }}>(sold {i.quantity})</span>
                  </span>
                  <span className="es-qtybox">
                    <button onClick={() => setQty(i.productId, i.quantity, String(q - 1))}>−</button>
                    <input value={q} onChange={(e) => setQty(i.productId, i.quantity, e.target.value)} />
                    <button onClick={() => setQty(i.productId, i.quantity, String(q + 1))}>+</button>
                  </span>
                  <span className="es-item-total">{formatMoney(refund)}</span>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="es-bottom">
        <div className="es-lastbill">{bill ? `INVOICE : ${bill.billNo}` : 'Select a bill'}</div>
        <div className="es-netbar">BILL AMT : {billAmt.toFixed(2)}</div>
      </div>

      <div className="es-actions">
        <button className="es-btn-orange" onClick={save} disabled={!bill || !returnLines.length}>
          Save
        </button>
        <button className="es-btn-grey" onClick={() => bill && setBill(null)} disabled={!bill}>
          {bill ? 'Back' : 'Print'}
        </button>
      </div>
    </div>
  );
};

export default SaleReturn;
